import React, { createContext, useContext, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

const FilterContext = createContext();

export const useFilters = () => {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error('useFilters must be used within a FilterProvider');
  }
  return context;
};

export const FilterProvider = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [filters, setFilters] = useState({
    sprint_id: searchParams.get('sprint_id') || '',
    release_id: searchParams.get('release_id') || '',
    portfolio_id: searchParams.get('portfolio_id') || '',
    program_id: searchParams.get('program_id') || '',
    project_id: searchParams.get('project_id') || '',
    team: searchParams.get('team') || '',
    status: searchParams.get('status') || '',
  });

  // Update URL when filters change - preserve params we don't manage
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      // List of filter keys we manage
      const managedKeys = ['sprint_id', 'release_id', 'portfolio_id', 'program_id', 'project_id', 'team', 'status'];
      
      // Remove all managed keys first
      managedKeys.forEach(key => params.delete(key));
      
      // Add back non-empty filter values
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.set(key, value);
      });
      
      return params;
    }, { replace: true });
  }, [filters, setSearchParams]);

  // Read from URL on initial load and URL changes
  useEffect(() => {
    setFilters({
      sprint_id: searchParams.get('sprint_id') || '',
      release_id: searchParams.get('release_id') || '',
      portfolio_id: searchParams.get('portfolio_id') || '',
      program_id: searchParams.get('program_id') || '',
      project_id: searchParams.get('project_id') || '',
      team: searchParams.get('team') || '',
      status: searchParams.get('status') || '',
    });
  }, [searchParams]);

  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value || '' }));
  };

  const clearFilters = () => {
    setFilters({
      sprint_id: '',
      release_id: '',
      portfolio_id: '',
      program_id: '',
      project_id: '',
      team: '',
      status: '',
    });
  };

  // Get clean filter params for API calls (removes empty values)
  const getFilterParams = () => {
    const params = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params[key] = value;
    });
    return params;
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== '');

  return (
    <FilterContext.Provider value={{
      filters,
      updateFilter,
      clearFilters,
      getFilterParams,
      hasActiveFilters,
    }}>
      {children}
    </FilterContext.Provider>
  );
};
